import { IconSync } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import { useUpdateTurnover } from '../../hooks/useUpdateTurnover';
import styles from '../SpendingLimitDrawer.module.css';

type FormValues = {
    turnover: string;
};

export const UpdateTurnover = ({
    refreshSpendingLimit,
    loadingRefreshSpendingLimit
}: {
    refreshSpendingLimit: () => Promise<void>;
    loadingRefreshSpendingLimit: boolean;
}) => {
    const {
        previousTurnover,
        updateTurnover,
        isLoading: loadingUpdateTurnover
    } = useUpdateTurnover();
    const methods = useForm({
        defaultValues: {
            turnover: String(previousTurnover).replace(
                /\B(?=(\d{3})+(?!\d))/g,
                ','
            )
        }
    });

    const handleSubmit = async ({ turnover }: FormValues) => {
        const sanitizedTurnover = turnover.replace(/,/g, '');
        await updateTurnover(Number(sanitizedTurnover));
        await refreshSpendingLimit();
    };

    const renderButtonText = () => {
        if (loadingUpdateTurnover) {
            return (
                <>
                    <IconSync className={styles.spinningIcon} />
                    Updating turnover...
                </>
            );
        }
        if (loadingRefreshSpendingLimit) {
            return (
                <>
                    <IconSync className={styles.spinningIcon} />
                    Checking limits...
                </>
            );
        }
        return 'See what you can spend';
    };

    const onSubmit = methods.handleSubmit(handleSubmit);

    return (
        <FormProvider {...methods}>
            <form
                className="border-t border-t-structure-80"
                onSubmit={onSubmit}
            >
                <div className="flex flex-col gap-s">
                    <div className="pt-l text-2xl font-bold">
                        First, we need to check some numbers
                    </div>
                    <div className="font-reg">
                        It's been a while since we asked you for an update.
                    </div>
                </div>
                <div className="pt-l">
                    <div className="text-m font-med">
                        Turnover for the last 12 months
                    </div>
                    <div className="text-s font-reg">
                        This doesn't have to be exact to the penny
                    </div>
                </div>
                <div className="relative flex items-center pt-m">
                    <span className="absolute left-m pb-[1px] text-m">£</span>
                    <input
                        name="turnover"
                        placeholder="Enter amount..."
                        type="text"
                        className="w-full rounded-s border border-structure-80 p-m pl-xl"
                        onChange={(e) => {
                            const sanitizedValue = e.target.value.replace(
                                /[^0-9]/g,
                                ''
                            );
                            const formattedValue = sanitizedValue.replace(
                                /\B(?=(\d{3})+(?!\d))/g,
                                ','
                            );
                            methods.setValue('turnover', formattedValue);
                        }}
                        value={methods.watch('turnover')}
                    />
                </div>
                <div className="pt-l">
                    <Button
                        className="w-full"
                        variant="primary"
                        disabled={
                            loadingUpdateTurnover || loadingRefreshSpendingLimit
                        }
                    >
                        {renderButtonText()}
                    </Button>
                </div>
            </form>
        </FormProvider>
    );
};
